import type {
  LoomAuthoringDocument,
  LoomFuselageStation,
  LoomSceneNode,
  LoomVec3,
  LoomWingPanel,
} from "../features/parametric-geometry/loomAuthoringModel";
import type { LoomWingBodyDefinition } from "../features/parametric-geometry/loomAuthoritativeDefinition";
import { parseLoomWingBodyDefinitionV2, type LoomWingBodyDefinitionV2 } from "./loomRebuildV3";

export const LOOM_REBUILD_SCHEMA_V4 = "loom.authoritative-rebuild-request/v4" as const;
export const LOOM_REBUILD_DEFINITION_SCHEMA_V4 = "loom.aircraft-occurrence-rebuild-definition/v1" as const;
export const LOOM_REBUILD_EVALUATOR_V4 = "loom.native-aircraft-occurrence-union/v1" as const;
export const LOOM_REBUILD_TOPOLOGY_V4 = "ordered-aircraft-occurrence-union" as const;

type Quaternion = [number, number, number, number];
type Rigid = { translationM: [number, number, number]; rotationQuaternionXyzw: Quaternion };

export interface LoomAircraftOccurrenceRebuildDefinition {
  schema: typeof LOOM_REBUILD_DEFINITION_SCHEMA_V4;
  sourceDefinitions: Array<{
    definitionId: string;
    definition: LoomWingBodyDefinition | LoomWingBodyDefinitionV2;
  }>;
  occurrences: Array<{
    occurrenceId: string;
    bodyId: string;
    sourceDefinitionId: string;
    role: "wing" | "fuselage";
    properRigid: Rigid;
    parity: "proper" | "reflected";
    reflection: null | {
      planeNormal: [number, number, number];
      planeOffsetM: number;
      orientationPolicy: "reverse-surface-orientation";
    };
  }>;
  unionGroups: Array<{
    unionGroupId: string;
    orderedOccurrenceIds: string[];
  }>;
}

export interface LoomAuthoritativeRebuildRequestV4 {
  schema: typeof LOOM_REBUILD_SCHEMA_V4;
  requestId: string;
  sourceDefinition: { documentId: string; revision: number; stateId: string };
  evaluatorId: typeof LOOM_REBUILD_EVALUATOR_V4;
  topology: typeof LOOM_REBUILD_TOPOLOGY_V4;
  definition: LoomAircraftOccurrenceRebuildDefinition;
}

type PrimitiveNode = Extract<LoomSceneNode, { type: "primitive" }>;
type Wing = Extract<PrimitiveNode["definition"], { primitive: "wing" }>;
type Fuselage = Extract<PrimitiveNode["definition"], { primitive: "fuselage" }>;

const TOKEN = /^[A-Za-z0-9][A-Za-z0-9._:/-]{0,159}$/;
const MAX_LENGTH_M = 1_000_000;
const IDENTITY: Rigid = { translationM: [0, 0, 0], rotationQuaternionXyzw: [0, 0, 0, 1] };

function compareIds(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function exact(value: unknown, keys: readonly string[], label: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${label} must be an object`);
  }
  const record = value as Record<string, unknown>;
  if (Object.keys(record).length !== keys.length
      || Object.keys(record).some((key) => !keys.includes(key))) {
    throw new Error(`${label} has an unsupported field set`);
  }
  return record;
}

function token(value: unknown, label: string): string {
  if (typeof value !== "string" || !TOKEN.test(value)) throw new Error(`${label} is invalid`);
  return value;
}

function finite(value: unknown, label: string, limit = MAX_LENGTH_M): number {
  if (typeof value !== "number" || !Number.isFinite(value) || Math.abs(value) > limit) {
    throw new Error(`${label} must be a bounded finite number`);
  }
  return Object.is(value, -0) ? 0 : value;
}

function vector(value: unknown, size: 3 | 4, label: string): number[] {
  if (!Array.isArray(value) || value.length !== size) throw new Error(`${label} is malformed`);
  return value.map((entry, index) => finite(entry, `${label}[${index}]`, 1_000_000));
}

function quaternionMultiply(left: Quaternion, right: Quaternion): Quaternion {
  const [lx, ly, lz, lw] = left; const [rx, ry, rz, rw] = right;
  return [
    lw * rx + lx * rw + ly * rz - lz * ry,
    lw * ry - lx * rz + ly * rw + lz * rx,
    lw * rz + lx * ry - ly * rx + lz * rw,
    lw * rw - lx * rx - ly * ry - lz * rz,
  ];
}

function canonicalQuaternion(value: Quaternion): Quaternion {
  const norm = Math.hypot(...value);
  if (!Number.isFinite(norm) || norm === 0) throw new Error("quaternion is invalid");
  const normalized = value.map((entry) => entry / norm) as Quaternion;
  const pivot = [...normalized].reverse().find((entry) => Math.abs(entry) > 1e-15) ?? 0;
  return pivot < 0 ? normalized.map((entry) => -entry) as Quaternion : normalized;
}

function rotate(value: LoomVec3, quaternion: Quaternion): [number, number, number] {
  const point: Quaternion = [value[0], value[1], value[2], 0];
  const inverse: Quaternion = [-quaternion[0], -quaternion[1], -quaternion[2], quaternion[3]];
  const result = quaternionMultiply(quaternionMultiply(quaternion, point), inverse);
  return [result[0], result[1], result[2]];
}

function compose(outer: Rigid, inner: Rigid): Rigid {
  const shifted = rotate(inner.translationM, outer.rotationQuaternionXyzw);
  return {
    translationM: [
      shifted[0] + outer.translationM[0],
      shifted[1] + outer.translationM[1],
      shifted[2] + outer.translationM[2],
    ],
    rotationQuaternionXyzw: canonicalQuaternion(quaternionMultiply(
      outer.rotationQuaternionXyzw, inner.rotationQuaternionXyzw,
    )),
  };
}

function rotation(axis: "x" | "y" | "z", angleDeg: number, origin: LoomVec3): Rigid {
  const angle = angleDeg * Math.PI / 180; const half = angle / 2;
  const sin = Math.sin(half); const cos = Math.cos(half);
  const quaternion: Quaternion = axis === "x" ? [sin, 0, 0, cos]
    : axis === "y" ? [0, sin, 0, cos] : [0, 0, sin, cos];
  const moved = rotate(origin, quaternion);
  return {
    translationM: [origin[0] - moved[0], origin[1] - moved[1], origin[2] - moved[2]],
    rotationQuaternionXyzw: quaternion,
  };
}

function supportedWing(value: Wing): LoomWingPanel | null {
  const panel = value.panels[0];
  return value.panels.length === 1 && value.tip === "flat" && value.tipRadiusM === 0
    && panel !== undefined && panel.sectionRecipes === undefined
    && panel.sectionSamples >= 11 && panel.sectionSamples <= 101 && panel.sectionSamples % 2 === 1
    && panel.yTipM > panel.yRootM && panel.chordRootM > 0 && panel.chordTipM > 0
    && Math.abs(panel.sweepDeg) < 89 && Math.abs(panel.dihedralDeg) < 89
    && panel.airfoilRoot.family === "naca4" && panel.airfoilTip.family === "naca4"
    ? panel : null;
}

function supportedFuselage(value: Fuselage): value is Fuselage & { stations: LoomFuselageStation[] } {
  return value.stations.length >= 3 && value.stations.length <= 257
    && value.stations[0]?.kind === "point"
    && value.stations[value.stations.length - 1]?.kind === "point"
    && value.stations.every((station, index) => index === 0 || station.xM > value.stations[index - 1]!.xM);
}

function sameRigid(left: Rigid, right: Rigid): boolean {
  return left.translationM.every((value, index) => value === right.translationM[index])
    && left.rotationQuaternionXyzw.every((value, index) => (
      value === right.rotationQuaternionXyzw[index]
    ));
}

interface CompiledPath {
  occurrence: LoomAircraftOccurrenceRebuildDefinition["occurrences"][number];
  source: PrimitiveNode;
  visited: Set<string>;
}

function compilePath(nodes: ReadonlyMap<string, LoomSceneNode>, occurrenceId: string): CompiledPath | null {
  let node = nodes.get(occurrenceId); let rigid = structuredClone(IDENTITY); let reflected = false;
  let reflection: CompiledPath["occurrence"]["reflection"] = null;
  const visited = new Set<string>();
  while (node?.type === "transform") {
    if (visited.has(node.id)) return null;
    visited.add(node.id);
    const operation = node.operation;
    if (operation.transform === "uniform-scale") return null;
    if (operation.transform === "mirror" || operation.transform === "mirror-y") {
      if (reflected) return null;
      const axis = operation.transform === "mirror-y" ? "y" : operation.axis;
      const offset = operation.transform === "mirror-y" ? 0 : operation.planeOffsetM;
      const normal: [number, number, number] = axis === "x" ? [1, 0, 0]
        : axis === "y" ? [0, 1, 0] : [0, 0, 1];
      reflected = true;
      reflection = {
        planeNormal: normal,
        planeOffsetM: offset,
        orientationPolicy: "reverse-surface-orientation",
      };
    } else if (operation.transform === "translate") {
      if (reflected) return null;
      rigid = compose(rigid, { translationM: [...operation.offsetM], rotationQuaternionXyzw: [0, 0, 0, 1] });
    } else {
      if (reflected) return null;
      rigid = compose(rigid, rotation(operation.axis, operation.angleDeg, operation.originM));
    }
    node = nodes.get(node.inputNodeId);
  }
  if (node?.type !== "primitive" || !["wing", "fuselage"].includes(node.definition.primitive)) return null;
  visited.add(node.id);
  return {
    source: node,
    visited,
    occurrence: {
      occurrenceId, bodyId: occurrenceId, sourceDefinitionId: "native-wing-body",
      role: node.definition.primitive as "wing" | "fuselage", properRigid: rigid,
      parity: reflected ? "reflected" : "proper", reflection,
    },
  };
}

export function deriveLoomAircraftOccurrenceRebuildDefinition(
  document: LoomAuthoringDocument,
): LoomAircraftOccurrenceRebuildDefinition | null {
  if (document.presetBinding || document.rootNodeIds.length !== 1) return null;
  const nodes = new Map(document.nodes.map((node) => [node.id, node]));
  const root = nodes.get(document.rootNodeIds[0]!);
  if (root?.type !== "boolean" || root.operation !== "union" || root.blendRadiusM !== undefined
      || root.inputNodeIds.length < 2 || root.inputNodeIds.length > 8) return null;
  const paths = root.inputNodeIds.map((id) => compilePath(nodes, id));
  if (paths.some((path) => path === null)) return null;
  const compiled = paths as CompiledPath[];
  const wings = compiled.filter(({ source }) => source.definition.primitive === "wing");
  const bodies = compiled.filter(({ source }) => source.definition.primitive === "fuselage");
  const wingSources = new Set(wings.map(({ source }) => source.id));
  const bodySources = new Set(bodies.map(({ source }) => source.id));
  const properWings = wings.filter(({ occurrence }) => occurrence.parity === "proper");
  const reflectedWings = wings.filter(({ occurrence }) => occurrence.parity === "reflected");
  const commonPose = compiled[0]?.occurrence.properRigid;
  if (compiled.length !== 3 || wings.length !== 2 || bodies.length !== 1
      || properWings.length !== 1 || reflectedWings.length !== 1
      || wingSources.size !== 1 || bodySources.size !== 1 || !commonPose
      || compiled.some(({ occurrence }) => !sameRigid(occurrence.properRigid, commonPose))) return null;
  const reflected = reflectedWings[0]!.occurrence.reflection;
  if (!reflected || reflected.planeOffsetM !== 0
      || reflected.planeNormal[0] !== 0 || reflected.planeNormal[1] !== 1
      || reflected.planeNormal[2] !== 0) return null;
  const wing = wings[0]!.source.definition as Wing; const fuselage = bodies[0]!.source.definition as Fuselage;
  const panel = supportedWing(wing);
  if (!panel || !supportedFuselage(fuselage)) return null;
  const visited = new Set<string>([root.id]); compiled.forEach((path) => path.visited.forEach((id) => visited.add(id)));
  if (visited.size !== document.nodes.length) return null;
  const { sectionRecipes: _sectionRecipes, ...normalizedPanel } = panel;
  const sourceDefinition: LoomWingBodyDefinition = {
    schema: "loom.wing-body-definition/v1",
    wing: {
      sourceNodeId: wings[0]!.source.id, instanceNodeId: wings[0]!.source.id,
      transformNodeIds: [], tip: "flat", tipRadiusM: 0,
      panel: normalizedPanel as LoomWingBodyDefinition["wing"]["panel"],
      placement: { kind: "translation", offsetM: [0, 0, 0] },
    },
    fuselage: {
      sourceNodeId: bodies[0]!.source.id,
      stations: fuselage.stations.map((station) => ({ ...station })),
    },
  };
  return {
    schema: LOOM_REBUILD_DEFINITION_SCHEMA_V4,
    sourceDefinitions: [{ definitionId: "native-wing-body", definition: sourceDefinition }],
    occurrences: compiled.map(({ occurrence }) => occurrence),
    unionGroups: [{ unionGroupId: root.id, orderedOccurrenceIds: [...root.inputNodeIds] }],
  };
}

export function parseLoomAuthoritativeRebuildRequestV4(
  value: unknown,
): LoomAuthoritativeRebuildRequestV4 {
  const root = exact(value, ["schema", "requestId", "sourceDefinition", "evaluatorId", "topology", "definition"], "v4 request");
  if (root.schema !== LOOM_REBUILD_SCHEMA_V4 || root.evaluatorId !== LOOM_REBUILD_EVALUATOR_V4
      || root.topology !== LOOM_REBUILD_TOPOLOGY_V4) throw new Error("v4 request markers are unsupported");
  const source = exact(root.sourceDefinition, ["documentId", "revision", "stateId"], "v4 sourceDefinition");
  const revision = source.revision;
  if (typeof revision !== "number" || !Number.isSafeInteger(revision) || revision < 0) throw new Error("v4 source revision is invalid");
  const definition = exact(root.definition, ["schema", "sourceDefinitions", "occurrences", "unionGroups"], "v4 definition");
  if (definition.schema !== LOOM_REBUILD_DEFINITION_SCHEMA_V4) throw new Error("v4 definition schema is unsupported");
  if (!Array.isArray(definition.sourceDefinitions) || definition.sourceDefinitions.length < 1 || definition.sourceDefinitions.length > 8
      || !Array.isArray(definition.occurrences) || definition.occurrences.length < 2 || definition.occurrences.length > 8
      || !Array.isArray(definition.unionGroups) || definition.unionGroups.length < 1 || definition.unionGroups.length > 4) {
    throw new Error("v4 definition exceeds its table budgets");
  }
  const sourceDefinitions = definition.sourceDefinitions.map((entry, index) => {
    const row = exact(entry, ["definitionId", "definition"], `sourceDefinitions[${index}]`);
    const sourceDefinition = row.definition as LoomWingBodyDefinition | LoomWingBodyDefinitionV2;
    if (sourceDefinition?.schema === "loom.wing-body-definition/v2") parseLoomWingBodyDefinitionV2(sourceDefinition);
    else if (sourceDefinition?.schema !== "loom.wing-body-definition/v1") throw new Error("source definition schema is unsupported");
    return { definitionId: token(row.definitionId, `sourceDefinitions[${index}].definitionId`), definition: sourceDefinition };
  });
  const sourceIds = sourceDefinitions.map(({ definitionId }) => definitionId);
  if (new Set(sourceIds).size !== sourceIds.length) throw new Error("source definition IDs must be unique");
  const occurrences = definition.occurrences.map((entry, index) => {
    const row = exact(entry, ["occurrenceId", "bodyId", "sourceDefinitionId", "role", "properRigid", "parity", "reflection"], `occurrences[${index}]`);
    const proper = exact(row.properRigid, ["translationM", "rotationQuaternionXyzw"], `occurrences[${index}].properRigid`);
    const translationM = vector(proper.translationM, 3, "translation") as [number, number, number];
    const quaternion = vector(proper.rotationQuaternionXyzw, 4, "quaternion") as Quaternion;
    const pivot = [...quaternion].reverse().find((entry) => Math.abs(entry) > 1e-15) ?? 0;
    if (Math.abs(Math.hypot(...quaternion) - 1) > 1e-9 || pivot < 0) {
      throw new Error("occurrence quaternion is not canonical unit length");
    }
    if (!sourceIds.includes(String(row.sourceDefinitionId)) || !["wing", "fuselage"].includes(String(row.role))) throw new Error("occurrence source or role is invalid");
    let reflection: LoomAircraftOccurrenceRebuildDefinition["occurrences"][number]["reflection"] = null;
    if (row.reflection !== null) {
      const receipt = exact(row.reflection, ["planeNormal", "planeOffsetM", "orientationPolicy"], `occurrences[${index}].reflection`);
      const normal = vector(receipt.planeNormal, 3, "reflection plane normal") as [number, number, number];
      if (Math.abs(Math.hypot(...normal) - 1) > 1e-9
          || receipt.orientationPolicy !== "reverse-surface-orientation") {
        throw new Error("reflection receipt is invalid");
      }
      reflection = {
        planeNormal: normal,
        planeOffsetM: finite(receipt.planeOffsetM, "reflection offset"),
        orientationPolicy: "reverse-surface-orientation",
      };
    }
    if ((row.parity === "proper") !== (reflection === null) || !["proper", "reflected"].includes(String(row.parity))) throw new Error("occurrence parity and reflection disagree");
    return {
      occurrenceId: token(row.occurrenceId, "occurrenceId"), bodyId: token(row.bodyId, "bodyId"),
      sourceDefinitionId: token(row.sourceDefinitionId, "sourceDefinitionId"), role: row.role as "wing" | "fuselage",
      properRigid: {
        translationM,
        rotationQuaternionXyzw: canonicalQuaternion(quaternion),
      },
      parity: row.parity as "proper" | "reflected", reflection,
    };
  });
  for (const key of ["occurrenceId", "bodyId"] as const) if (new Set(occurrences.map((row) => row[key])).size !== occurrences.length) throw new Error(`${key} values must be unique`);
  const occurrenceIds = occurrences.map(({ occurrenceId }) => occurrenceId); const assigned: string[] = [];
  const unionGroups = definition.unionGroups.map((entry, index) => {
    const row = exact(entry, ["unionGroupId", "orderedOccurrenceIds"], `unionGroups[${index}]`);
    if (!Array.isArray(row.orderedOccurrenceIds) || row.orderedOccurrenceIds.length < 2 || row.orderedOccurrenceIds.length > 8) throw new Error("union group occurrence budget is invalid");
    const ids = row.orderedOccurrenceIds.map((id) => token(id, "union occurrence ID"));
    if (new Set(ids).size !== ids.length || ids.some((id) => !occurrenceIds.includes(id))) throw new Error("union group occurrence identities are invalid");
    const roles = ids.map((id) => occurrences.find((row) => row.occurrenceId === id)!.role);
    if (!roles.includes("wing") || !roles.includes("fuselage")) throw new Error("each union group needs a wing and fuselage");
    assigned.push(...ids); return { unionGroupId: token(row.unionGroupId, "unionGroupId"), orderedOccurrenceIds: ids };
  });
  if (assigned.length !== occurrenceIds.length || new Set(assigned).size !== assigned.length) throw new Error("every occurrence must belong to exactly one union group");
  return {
    schema: LOOM_REBUILD_SCHEMA_V4,
    requestId: token(root.requestId, "requestId"),
    sourceDefinition: { documentId: token(source.documentId, "documentId"), revision, stateId: token(source.stateId, "stateId") },
    evaluatorId: LOOM_REBUILD_EVALUATOR_V4, topology: LOOM_REBUILD_TOPOLOGY_V4,
    definition: {
      schema: LOOM_REBUILD_DEFINITION_SCHEMA_V4,
      sourceDefinitions: sourceDefinitions.sort((left, right) => compareIds(left.definitionId, right.definitionId)),
      occurrences: occurrences.sort((left, right) => compareIds(left.occurrenceId, right.occurrenceId)),
      unionGroups: unionGroups.sort((left, right) => compareIds(left.unionGroupId, right.unionGroupId)),
    },
  };
}
